import { faArrowLeft, faRotateRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Select } from '@mantine/core'
import { DASHBOARD_FONT_FAMILY } from 'App/dashboards/_utils/dashboardFont'
import {
  DashboardHeaderContext,
  type TDashboardHeaderApi,
} from 'App/dashboards/_utils/dashboardHeaderContext'
import { StickyOffsetContext } from 'App/dashboards/_utils/stickyOffsetContext'
import type { TPropertyOption } from 'App/dashboards/_utils/useDashboardPropertyOptions'
import { MultiSelect } from 'components/MultiSelect/MultiSelect'
import type { TOptions } from 'components/MultiSelect/MultiSelect.types'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { colors, dashboardColors } from 'settings/colors'
import { SharedSettings } from 'settings/SharedSettings'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { pushRoute } from 'utils/pushRoute'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'

export type TDashboardShellProps = {
  title: string
  lastUpdated: Date | null
  onRefresh: () => void
  // Property dropdown options + selection + handler, supplied by the board (PestDashboard drives its
  // own per-dashboard, persisted selection via useDashboardPreferences).
  propertyOptions: TPropertyOption[]
  selectedPropertyIds: string[]
  onPropertyChange: (values: string[]) => void
  // Single picker (one property, pest — Mantine Select) or multi picker (several properties, water —
  // Hub's in-house components/MultiSelect). Both speak the same string[] selection contract.
  propertyPickerMode?: 'single' | 'multi'
  // Opt in to the sticky chrome: the header pins and compacts once the user scrolls into the data,
  // and the card headers, property banners and column headers beneath it pin in turn. Off by default.
  compactHeaderOnScroll?: boolean
  // The widget owns its own scroll region, so the shell is exactly one viewport tall and the page
  // itself doesn't scroll. Separate from the above: a page-scrolled dashboard wants the sticky
  // chrome too, and locking its height would leave it unable to scroll at all.
  widgetOwnsScroll?: boolean
  // Optional controls rendered in the header just before the property picker (e.g. the water
  // dashboards' shared date-range picker — the pest chart carries its own per-widget instead).
  headerActions?: ReactNode
  children: ReactNode
}

// Design: "Last updated Jun 10, 2026 9:34 AM" — date before time, no separator, uppercase meridiem,
// and always an explicit date. The previous format led with the time and collapsed today's date to
// "Today", which read as a different kind of value depending on when you looked at it.

// Above the cards it scrolls over, and above their internal stacking contexts.
const HEADER_Z = 10
// The header tightens its padding once you scroll into the data — content is unchanged, so only the
// padding moves. With the 39px content row that gives the design's 64px and 48px heights.
const HEADER_PADDING_FULL = { top: 12, bottom: 13 }
const HEADER_PADDING_COMPACT = { top: 4, bottom: 5 }
const HEADER_CONTENT_HEIGHT = 39
const HEADER_HEIGHT_FULL = HEADER_CONTENT_HEIGHT + HEADER_PADDING_FULL.top + HEADER_PADDING_FULL.bottom

const HEADER_HEIGHT_COMPACT =
  HEADER_CONTENT_HEIGHT + HEADER_PADDING_COMPACT.top + HEADER_PADDING_COMPACT.bottom

const formatLastUpdated = (lastUpdated: Date | null) => {
  if (!lastUpdated) return ''

  return translate.dates.format(moment.tz(lastUpdated, moment.tz.guess()), 'MMM D, YYYY h:mm a')
}

export const DashboardShell = ({
  title,
  lastUpdated,
  onRefresh,
  propertyOptions,
  selectedPropertyIds,
  onPropertyChange,
  propertyPickerMode = 'single',
  compactHeaderOnScroll = false,
  widgetOwnsScroll = false,
  headerActions,
  children,
}: TDashboardShellProps) => {
  const [widgetScrolled, setWidgetScrolled] = useState(false)
  const [pageScrolled, setPageScrolled] = useState(false)
  const selectClickToggle = useSelectClickToggle()
  // Identity-stable, so the widget's reporting effect doesn't re-fire every render.

  // The widget normally owns the scrolling, but on a short viewport it hits its minimum height and
  // the page scrolls too. Honour both, or the header would sit full-height exactly when space is
  // tightest.
  useEffect(() => {
    if (!compactHeaderOnScroll) return

    const onScroll = () => setPageScrolled(window.scrollY > 0)

    onScroll()

    window.addEventListener('scroll', onScroll, { passive: true })

    return () => window.removeEventListener('scroll', onScroll)
  }, [compactHeaderOnScroll])

  const compact = compactHeaderOnScroll && (widgetScrolled || pageScrolled)
  const headerHeight = compact ? HEADER_HEIGHT_COMPACT : HEADER_HEIGHT_FULL
  // The first rung of the sticky chain: null when the dashboard hasn't opted in, so nothing pins.
  const headerStickyOffset = compactHeaderOnScroll ? headerHeight : null

  const headerApi = useMemo<TDashboardHeaderApi>(
    // setScrolled is the raw state setter, so its identity is stable even as `compact` flips — the
    // widget's scroll listener depends on it and must not be torn down and rebuilt each time.
    () => ({ setScrolled: setWidgetScrolled, compact }),
    [compact],
  )

  const padding = compact ? HEADER_PADDING_COMPACT : HEADER_PADDING_FULL
  const groupPropertyOptions = new Set(propertyOptions.map((o) => o.group)).size > 1

  return (
    <DashboardHeaderContext.Provider value={compactHeaderOnScroll ? headerApi : null}>
      <div
        css={{
          background: dashboardColors.pageBg,
          // When the widget owns the scrolling, the shell is exactly one viewport tall and nothing
          // below it scrolls the page — that's what lets the table flex into the leftover space
          // rather than computing it. Page-scrolled dashboards must keep their natural height.
          ...(widgetOwnsScroll ? { height: '100vh', overflow: 'hidden' } : { minHeight: '100vh' }),
          display: 'flex',
          flexDirection: 'column',
          // Roboto for the whole dashboard — cascades to the header, widget cards, and tables.
          fontFamily: DASHBOARD_FONT_FAMILY,
        }}
      >
        {/* The header has no fill or bottom rule of its own — it sits straight on the page, and the
          only white in it is the Refresh control. Padding + a 39px content row give the design's
          64px overall height.

          Sticky, so it stays available while you read down a long table. It does need the page's
          own background once pinned: with a transparent background the content would scroll
          visibly underneath it. Same colour as the page, so it still reads as no background. */}
        <div
          css={{
            position: 'sticky',
            top: 0,
            zIndex: HEADER_Z,
            background: dashboardColors.pageBg,
            padding: `${padding.top}px 24px ${padding.bottom}px`,
            transition: 'padding 150ms ease',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minHeight: HEADER_CONTENT_HEIGHT,
            boxSizing: 'border-box',
            flexWrap: 'wrap',
          }}
        >
          <button
            aria-label={translate.phrases.banyanApp('Back')}
            onClick={() => pushRoute('/dashboards')}
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              background: 'transparent',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              padding: 0,
              color: dashboardColors.foreground,
            }}
          >
            <FontAwesomeIcon icon={faArrowLeft} css={{ fontSize: 18 }} />
          </button>
          <h1
            css={{
              fontSize: 20,
              lineHeight: '22.5px',
              fontWeight: 500,
              margin: 0,
              color: dashboardColors.foreground,
            }}
          >
            {title}
          </h1>

          {lastUpdated && (
            <>
              {/* Vertical rule separating the title from the timestamp, per the design. */}
              <div css={{ alignSelf: 'stretch', width: 1, background: dashboardColors.border }} />
              <span css={{ fontSize: 16, color: dashboardColors.lastUpdatedText }}>
                {translate.phrases.banyanApp('Last updated')} {formatLastUpdated(lastUpdated)}
              </span>
            </>
          )}

          <button
            onClick={onRefresh}
            // The design's shadcn "outline" button: 8px radius, 8/16 padding, a hairline input border
            // and the xs drop shadow.
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: colors.white,
              border: `1px solid ${dashboardColors.border}`,
              borderRadius: 8,
              boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.05)',
              padding: '8px 16px',
              cursor: 'pointer',
              color: dashboardColors.foreground,
              fontSize: 14,
              lineHeight: '20px',
              fontWeight: 500,
            }}
          >
            <FontAwesomeIcon icon={faRotateRight} css={{ fontSize: 16 }} />
            {translate.phrases.banyanApp('Refresh')}
          </button>

          <div css={{ flex: 1 }} />

          {headerActions}

          {propertyPickerMode === 'multi' ? (
            // Hub's in-house multi-select (the same control Reports/Alerts use to pick properties), so
            // the dashboard property picker stays visually consistent with the rest of the app. The
            // shell's external contract is still string[]; we map to/from the component's TOptions here.
            <div css={{ width: 280 }}>
              <MultiSelect
                data={propertyOptions}
                selectedData={propertyOptions.filter((o) => selectedPropertyIds.includes(o.value))}
                onChange={(selected: TOptions[]) => onPropertyChange(selected.map((o) => String(o.value)))}
                placeholder={translate.phrases.banyanApp('Select Properties')}
                groupOptions={groupPropertyOptions}
              />
            </div>
          ) : (
            <Select
              {...selectClickToggle}
              data={propertyOptions}
              value={selectedPropertyIds[0] ?? null}
              onChange={(value) => onPropertyChange(value ? [value] : [])}
              placeholder={translate.phrases.banyanApp('Select Property')}
              searchable
              // Token search (each space-separated term matched anywhere), the exact filter the Hub
              // property dropdowns use, so "cro zirkle" / "z cro" find "Zirkle - CRO".
              filter={mantineSelectSearchFilterProp}
              comboboxProps={{ withinPortal: true }}
              css={{ width: 220 }}
              styles={{
                ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER,
                input: { ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER.input, paddingRight: 28 },
              }}
            />
          )}
        </div>

        <div
          css={{
            // No gap under the header when it's pinned: the design has the card butting straight
            // against it (page background ends at 47px, card starts at 48). Dashboards that scroll
            // the page normally keep the 16px breathing room.
            padding: widgetOwnsScroll ? '0 24px 24px' : '16px 24px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            flex: 1,
            // Without this a flex item refuses to shrink below its content, so a tall table would
            // push the column past the viewport instead of scrolling inside it.
            minHeight: 0,
          }}
        >
          <StickyOffsetContext.Provider value={headerStickyOffset}>{children}</StickyOffsetContext.Provider>
        </div>
      </div>
    </DashboardHeaderContext.Provider>
  )
}
